/**
 * Milestones Page
 * BuilderBel Construction Project Management System
 */

import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  Flag,
  Milestone as MilestoneIcon,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { MilestoneDrawer } from '../components/milestones';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useCompleteMilestoneMutation,
  useDeleteMilestoneMutation,
  useGetMilestonesQuery,
  useUpdateMilestoneProgressMutation,
} from '../services/milestoneApi';
import type { Milestone, MilestoneStatus, MilestoneType } from '../types/milestone';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'project', label: 'Project' },
  { value: 'contractual', label: 'Contractual' },
  { value: 'payment', label: 'Payment' },
  { value: 'permit', label: 'Permit' },
  { value: 'owner', label: 'Owner' },
  { value: 'internal', label: 'Internal' },
];

const PAYMENT_OPTIONS = [
  { value: '', label: 'All Milestones' },
  { value: 'true', label: 'Payment Only' },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusVariant = (
  status: MilestoneStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'at_risk':
      return 'warning';
    case 'delayed':
      return 'error';
    case 'cancelled':
      return 'neutral';
    case 'not_started':
    default:
      return 'neutral';
  }
};

const getStatusLabel = (status: MilestoneStatus): string => {
  const labels: Record<MilestoneStatus, string> = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    at_risk: 'At Risk',
    delayed: 'Delayed',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return labels[status] || status;
};

const getTypeLabel = (type: MilestoneType): string => {
  const labels: Record<MilestoneType, string> = {
    project: 'Project',
    contractual: 'Contractual',
    payment: 'Payment',
    permit: 'Permit',
    owner: 'Owner',
    internal: 'Internal',
  };
  return labels[type] || type;
};

// Risk level dot color
const getRiskDotClass = (riskLevel: string): string => {
  switch (riskLevel) {
    case 'low':
      return 'bg-green-500';
    case 'medium':
      return 'bg-yellow-500';
    case 'high':
      return 'bg-orange-500';
    case 'critical':
      return 'bg-red-600';
    default:
      return 'bg-gray-300';
  }
};

// ============================================================================
// PROGRESS BAR
// ============================================================================

interface ProgressBarProps {
  progress: number;
  status: MilestoneStatus;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ progress, status }) => {
  const barColorClass =
    status === 'completed'
      ? 'bg-green-500'
      : status === 'at_risk'
        ? 'bg-yellow-500'
        : status === 'delayed'
          ? 'bg-red-500'
          : 'bg-brand-600';

  return (
    <div className="flex items-center space-x-2 min-w-0">
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${barColorClass} transition-all duration-300`}
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap w-8 text-right">{progress}%</span>
    </div>
  );
};

// ============================================================================
// COMPLETE MILESTONE MODAL (inline)
// ============================================================================

interface CompleteMilestoneModalProps {
  milestone: Milestone;
  onConfirm: (actualDate: string, notes: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const CompleteMilestoneModal: React.FC<CompleteMilestoneModalProps> = ({
  milestone,
  onConfirm,
  onCancel,
  isLoading,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const [actualDate, setActualDate] = useState(today);
  const [notes, setNotes] = useState('');

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[110]" onClick={onCancel} />
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-green-50 rounded-full">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Complete Milestone</h3>
              <p className="text-xs text-gray-500">{milestone.name}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <DatePicker
                label="Actual Completion Date"
                value={actualDate}
                onChange={date => setActualDate(date || '')}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional completion notes..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-5">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(actualDate, notes)}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              disabled={isLoading || !actualDate}
            >
              {isLoading ? 'Completing...' : 'Mark Complete'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const Milestones: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<MilestoneStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<MilestoneType | ''>('');
  const [paymentFilter, setPaymentFilter] = useState<'' | 'true'>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // Complete modal state
  const [completingMilestone, setCompletingMilestone] = useState<Milestone | null>(null);

  // Action menu state
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API
  const {
    data: milestonesResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetMilestonesQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        isPayment: paymentFilter === 'true' ? true : undefined,
        sortBy: 'targetDate',
        sortOrder: 'asc',
      },
    },
    { skip: !projectId }
  );

  const [deleteMilestone, { isLoading: isDeleting }] = useDeleteMilestoneMutation();
  const [completeMilestone, { isLoading: isCompleting }] = useCompleteMilestoneMutation();
  const [updateProgress] = useUpdateMilestoneProgressMutation();

  // Extract data
  const milestones = useMemo(
    () => milestonesResponse?.data?.milestones || [],
    [milestonesResponse]
  );
  const pagination = milestonesResponse?.data?.pagination;
  const stats = milestonesResponse?.data?.stats;

  // Header stats
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total',
        value: stats.total,
      },
      {
        title: 'Completed',
        value: stats.completed,
        variant: 'success' as const,
      },
      {
        title: 'At Risk',
        value: stats.atRisk,
        variant: 'warning' as const,
      },
      {
        title: 'Delayed',
        value: stats.delayed,
        variant: 'danger' as const,
      },
      {
        title: 'Payment Value',
        value: formatCurrency(stats.paymentTotal),
        variant: 'brand' as const,
      },
    ];
  }, [stats]);

  // Handlers
  const handleOpenCreate = useCallback(() => {
    setSelectedMilestone(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleOpenEdit = useCallback((milestone: Milestone) => {
    setSelectedMilestone(milestone);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleOpenView = useCallback((milestone: Milestone) => {
    setSelectedMilestone(milestone);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (milestone: Milestone) => {
      if (!projectId) return;
      if (!window.confirm(`Delete milestone "${milestone.name}"? This cannot be undone.`)) return;

      try {
        await deleteMilestone({ projectId, milestoneId: milestone._id }).unwrap();
        toast.success('Milestone deleted successfully');
      } catch {
        toast.error('Failed to delete milestone');
      }
    },
    [projectId, deleteMilestone, toast]
  );

  const handleComplete = useCallback((milestone: Milestone) => {
    setCompletingMilestone(milestone);
  }, []);

  const handleConfirmComplete = useCallback(
    async (actualDate: string, notes: string) => {
      if (!projectId || !completingMilestone) return;

      try {
        await completeMilestone({
          projectId,
          milestoneId: completingMilestone._id,
          data: { actualDate, notes: notes || undefined },
        }).unwrap();
        toast.success('Milestone completed successfully');
        setCompletingMilestone(null);
      } catch {
        toast.error('Failed to complete milestone');
      }
    },
    [projectId, completingMilestone, completeMilestone, toast]
  );

  // Build action menu items
  const getActionItems = useCallback(
    (milestone: Milestone): (ActionMenuItem | 'divider')[] => {
      const isCompleted = milestone.status === 'completed';
      const isCancelled = milestone.status === 'cancelled';
      const isEditable = !isCompleted && !isCancelled;

      return [
        {
          label: 'View Details',
          icon: <MilestoneIcon className="h-4 w-4" />,
          onClick: () => handleOpenView(milestone),
        },
        {
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleOpenEdit(milestone),
          disabled: !isEditable,
          hidden: !isEditable,
        },
        'divider',
        {
          label: 'Mark Complete',
          icon: <CheckCircle2 className="h-4 w-4" />,
          onClick: () => handleComplete(milestone),
          variant: 'success',
          disabled: !isEditable,
          hidden: isCompleted,
        },
        'divider',
        {
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(milestone),
          variant: 'danger',
        },
      ];
    },
    [handleOpenView, handleOpenEdit, handleComplete, handleDelete]
  );

  // Reset page when filters change
  const handleStatusChange = (val: string) => {
    setStatusFilter(val as MilestoneStatus | '');
    setPage(1);
  };

  const handleTypeChange = (val: string) => {
    setTypeFilter(val as MilestoneType | '');
    setPage(1);
  };

  const handlePaymentChange = (val: string) => {
    setPaymentFilter(val as '' | 'true');
    setPage(1);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <ModuleLayout
      title={projectId ? 'Project Milestones' : 'All Milestones'}
      description={
        projectId
          ? 'Track milestones and key deliverables for this project'
          : 'Manage milestones across all projects'
      }
      icon={<MilestoneIcon className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Add Milestone',
        onClick: handleOpenCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={headerStats}
    >
      {/* Filters */}
      <div className="flex items-center space-x-3 mb-4 flex-wrap gap-y-2">
        <div className="w-40">
          <SearchableDropdown
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={handleStatusChange}
            placeholder="All Statuses"
          />
        </div>
        <div className="w-40">
          <SearchableDropdown
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={handleTypeChange}
            placeholder="All Types"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={PAYMENT_OPTIONS}
            value={paymentFilter}
            onChange={handlePaymentChange}
            placeholder="All Milestones"
          />
        </div>

        <button
          onClick={() => refetch()}
          className="ml-auto p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      ) : milestones.length === 0 ? (
        <EmptyState
          icon={MilestoneIcon}
          title="No milestones found"
          description="Track project milestones, key deliverables, and timeline views to monitor project progress."
          action={{
            label: 'Add Milestone',
            onClick: handleOpenCreate,
          }}
        />
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">
                      Progress
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Target Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Forecast
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Risk
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Payment $
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {milestones.map(milestone => (
                    <tr
                      key={milestone._id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleOpenView(milestone)}
                    >
                      {/* Number */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-mono text-gray-500">
                          {milestone.milestoneNumber}
                        </span>
                      </td>

                      {/* Name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          {milestone.isPaymentMilestone && (
                            <DollarSign className="h-3.5 w-3.5 text-brand-600 shrink-0" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900 line-clamp-1">
                              {milestone.name}
                            </p>
                            {milestone.category && (
                              <p className="text-xs text-gray-400">{milestone.category}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs text-gray-600">
                          {getTypeLabel(milestone.type)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusBadge
                          status={getStatusLabel(milestone.status)}
                          variant={getStatusVariant(milestone.status)}
                        />
                      </td>

                      {/* Progress */}
                      <td className="px-4 py-3 w-32">
                        <ProgressBar progress={milestone.progress} status={milestone.status} />
                      </td>

                      {/* Target Date */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-xs text-gray-700">
                            {formatDate(milestone.targetDate)}
                          </span>
                        </div>
                      </td>

                      {/* Forecast */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs text-gray-500">
                          {milestone.forecastDate ? formatDate(milestone.forecastDate) : '-'}
                        </span>
                      </td>

                      {/* Risk Level */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-1.5">
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${getRiskDotClass(milestone.riskLevel)}`}
                          />
                          <span className="text-xs text-gray-600 capitalize">
                            {milestone.riskLevel}
                          </span>
                        </div>
                      </td>

                      {/* Payment Amount */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {milestone.isPaymentMilestone && milestone.paymentAmount ? (
                          <span className="text-xs font-medium text-brand-700">
                            {formatCurrency(milestone.paymentAmount)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <ActionMenu
                          id={milestone._id}
                          items={getActionItems(milestone)}
                          isOpen={activeMenuId === milestone._id}
                          onOpenChange={open => setActiveMenuId(open ? milestone._id : null)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} milestones
              </p>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-600 px-2">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Milestone Drawer */}
      <MilestoneDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedMilestone(null);
        }}
        milestone={selectedMilestone}
        mode={drawerMode}
        onSuccess={() => refetch()}
      />

      {/* Complete Milestone Modal */}
      {completingMilestone && (
        <CompleteMilestoneModal
          milestone={completingMilestone}
          onConfirm={handleConfirmComplete}
          onCancel={() => setCompletingMilestone(null)}
          isLoading={isCompleting}
        />
      )}
    </ModuleLayout>
  );
};

export default Milestones;
